import { Plus, Trash2, Cake, Heart } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const blank = () => ({ id: Math.random().toString(36).slice(2), name: "", type: "birthday", month: "", day: "", year: "", note: "" });

export const CelebrationsEditor = ({ value = [], onChange }) => {
  const rows = Array.isArray(value) ? value : [];
  const update = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const add = () => onChange([...rows, blank()]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2" data-testid="celebrations-editor">
      {rows.map((r, i) => (
        <div key={r.id || i} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-2.5" data-testid={`celebration-row-${i}`}>
          <div className="flex items-center gap-2">
            <Input value={r.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="Who (e.g. Jordyn)" className="h-9 flex-1 border-zinc-800 bg-zinc-900 text-sm" data-testid={`celebration-name-${i}`} />
            <Select value={r.type} onValueChange={(v) => update(i, { type: v })}>
              <SelectTrigger className="h-9 w-[130px] border-zinc-800 bg-zinc-900 text-sm" data-testid={`celebration-type-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-950">
                <SelectItem value="birthday">🎂 Birthday</SelectItem>
                <SelectItem value="anniversary">💍 Anniversary</SelectItem>
              </SelectContent>
            </Select>
            <button onClick={() => remove(i)} className="p-1.5 text-zinc-500 hover:text-red-400" data-testid={`celebration-remove-${i}`} aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Select value={r.month ? String(r.month) : ""} onValueChange={(v) => update(i, { month: v })}>
              <SelectTrigger className="h-9 w-[90px] border-zinc-800 bg-zinc-900 text-sm" data-testid={`celebration-month-${i}`}><SelectValue placeholder="Month" /></SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-950">
                {MONTHS.map((m, idx) => <SelectItem key={m} value={String(idx + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input value={r.day} onChange={(e) => update(i, { day: e.target.value.replace(/[^\d]/g, "").slice(0, 2) })} placeholder="Day" className="h-9 w-[70px] border-zinc-800 bg-zinc-900 text-sm" data-testid={`celebration-day-${i}`} />
            <Input value={r.year || ""} onChange={(e) => update(i, { year: e.target.value.replace(/[^\d]/g, "").slice(0, 4) })} placeholder="Year (opt)" className="h-9 w-[100px] border-zinc-800 bg-zinc-900 text-sm" data-testid={`celebration-year-${i}`} />
          </div>
          <Input value={r.note || ""} onChange={(e) => update(i, { note: e.target.value })} placeholder="Note (optional — e.g. loves Elsa)" className="mt-2 h-9 border-zinc-800 bg-zinc-900 text-sm" data-testid={`celebration-note-${i}`} />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="border-dashed border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800" data-testid="celebration-add">
        <Plus className="mr-1.5 h-4 w-4" /> Add a celebration
      </Button>
    </div>
  );
};

// Read-only chips shown on the profile.
export const CelebrationsList = ({ value = [] }) => {
  const rows = (Array.isArray(value) ? value : []).filter((r) => r.name && r.month && r.day);
  if (!rows.length) return null;
  const MO = ["", ...MONTHS];
  return (
    <div className="flex flex-wrap gap-2" data-testid="celebrations-list">
      {rows.map((r, i) => (
        <span key={r.id || i} className="inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-zinc-900/60 px-3 py-1 text-xs text-zinc-200" data-testid={`celebration-chip-${i}`}>
          {r.type === "anniversary" ? <Heart className="h-3.5 w-3.5 text-pink-400" /> : <Cake className="h-3.5 w-3.5 text-amber-400" />}
          <span className="font-600">{r.name}</span>
          <span className="text-zinc-400">{MO[+r.month] || "?"} {r.day}{r.year ? `, ${r.year}` : ""}</span>
        </span>
      ))}
    </div>
  );
};
